/**
 * Evéllae — in-page collection navigation.
 * Category links stay real <a href> URLs (shareable, open in new tab, crawlable).
 * When JS is available, clicking one fetches that collection page and swaps
 * #MainContent in place, keeping the header, then updates the URL and history.
 * Any failure falls back to a normal page load.
 */
(() => {
  if (window.__ccnReady) return;
  window.__ccnReady = true;

  const NAV = '[data-ccn]';
  const LINK = 'a[data-ccn-link]';
  const CACHE_TTL = 60 * 1000;
  const cache = new Map();
  let controller = null;
  let current = location.href;

  const html = document.documentElement;
  const keyOf = (href) => {
    const u = new URL(href, location.href);
    u.hash = '';
    return u.pathname + u.search;
  };
  const isCollectionPath = (pathname) => /\/collections\/[^/]+\/?$/.test(pathname);

  // Track every URL change (including the theme's filter pushState) so popstate can tell real changes from hash jumps.
  for (const method of ['pushState', 'replaceState']) {
    const original = history[method];
    history[method] = function (...args) {
      const result = original.apply(this, args);
      current = location.href;
      return result;
    };
  }

  function fetchPage(href, signal) {
    const key = keyOf(href);
    const hit = cache.get(key);
    if (hit && Date.now() - hit.time < CACHE_TTL) return hit.promise;
    const promise = fetch(href, { signal, credentials: 'same-origin', headers: { Accept: 'text/html' } }).then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.text();
    });
    cache.set(key, { time: Date.now(), promise });
    promise.catch(() => cache.delete(key));
    if (cache.size > 12) cache.delete(cache.keys().next().value);
    return promise;
  }

  function prefetch(href) {
    try {
      fetchPage(href).catch(() => {});
    } catch (e) {
      /* ignore */
    }
  }

  function runScripts(root) {
    root.querySelectorAll('script').forEach((old) => {
      const type = (old.getAttribute('type') || '').toLowerCase();
      if (type.includes('json') || type === 'importmap') return;
      const script = document.createElement('script');
      for (const attr of old.attributes) script.setAttribute(attr.name, attr.value);
      script.textContent = old.textContent;
      old.replaceWith(script);
    });
  }

  function swap(main, newMain) {
    for (const attr of Array.from(main.attributes)) {
      if (attr.name !== 'id' && !newMain.hasAttribute(attr.name)) main.removeAttribute(attr.name);
    }
    for (const attr of newMain.attributes) main.setAttribute(attr.name, attr.value);
    const nodes = Array.from(newMain.childNodes).map((node) => document.importNode(node, true));
    main.replaceChildren(...nodes);
    runScripts(main);
  }

  function headerOffset() {
    const value = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height'));
    return Number.isFinite(value) ? value : 0;
  }

  function scrollToNav() {
    const nav = document.querySelector(NAV);
    if (!nav) return;
    const top = nav.getBoundingClientRect().top + window.scrollY - headerOffset() - 8;
    if (window.scrollY > top) window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
  }

  function announce(doc) {
    const status = document.querySelector('[data-ccn-status]');
    const title = document.querySelector('[data-ccn-title]');
    if (status && title) status.textContent = title.textContent.trim();
  }

  async function go(href, { push = true, scroll = null } = {}) {
    const main = document.getElementById('MainContent');
    if (!main || !document.querySelector(NAV)) {
      location.href = href;
      return;
    }

    if (controller) controller.abort();
    controller = new AbortController();
    const { signal } = controller;

    if (push) {
      try {
        history.replaceState({ ...(history.state || {}), ccnScroll: window.scrollY }, '', location.href);
      } catch (e) {
        /* ignore */
      }
    }

    html.classList.add('ccn-loading');
    let text;
    try {
      text = await (cache.has(keyOf(href)) ? fetchPage(href) : fetchPage(href, signal));
    } catch (error) {
      if (signal.aborted) return;
      location.href = href;
      return;
    }
    if (signal.aborted) return;

    const doc = new DOMParser().parseFromString(text, 'text/html');
    const newMain = doc.getElementById('MainContent');
    if (!newMain || !newMain.querySelector(NAV)) {
      location.href = href;
      return;
    }

    if (push) history.pushState({ ccn: true }, '', href);

    const apply = () => {
      swap(main, newMain);
      if (doc.title) document.title = doc.title;
    };
    if (document.startViewTransition && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      try {
        await document.startViewTransition(apply).updateCallbackDone;
      } catch (e) {
        apply();
      }
    } else {
      apply();
    }

    html.classList.remove('ccn-loading');

    if (typeof scroll === 'number') {
      window.scrollTo({ top: scroll, behavior: 'instant' });
    } else if (push) {
      scrollToNav();
      const title = document.querySelector('[data-ccn-title]');
      if (title) title.focus({ preventScroll: true });
    }
    announce();
    document.dispatchEvent(new CustomEvent('ccn:navigated', { detail: { url: href } }));
  }

  // Clicks on category links
  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest(LINK) : null;
    if (link) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.target && link.target !== '_self') return;
      const url = new URL(link.href, location.href);
      if (url.origin !== location.origin) return;
      event.preventDefault();
      if (keyOf(url.href) === keyOf(location.href)) return;
      go(url.href, { push: true });
      return;
    }

    const more = event.target instanceof Element ? event.target.closest('[data-ccn-more]') : null;
    if (more) {
      const wrap = more.closest('[data-ccn-subs]');
      const open = more.getAttribute('aria-expanded') !== 'true';
      more.setAttribute('aria-expanded', String(open));
      if (wrap) wrap.classList.toggle('is-open', open);
      const label = more.querySelector('[data-ccn-more-text]');
      if (label) label.textContent = open ? more.dataset.labelClose : more.dataset.labelOpen;
    }
  });

  // Warm the cache on intent
  const onIntent = (event) => {
    const link = event.target instanceof Element ? event.target.closest(LINK) : null;
    if (link) prefetch(link.href);
  };
  document.addEventListener('pointerenter', onIntent, true);
  document.addEventListener('focusin', onIntent);
  document.addEventListener('touchstart', onIntent, { passive: true });

  // Back / forward: restore category and filters for any collection URL in this flow
  window.addEventListener('popstate', (event) => {
    const previous = current;
    current = location.href;
    if (!document.querySelector(NAV)) return;
    if (!isCollectionPath(location.pathname)) return;
    if (keyOf(previous) === keyOf(location.href)) return; // hash-only change
    const scroll = event.state && typeof event.state.ccnScroll === 'number' ? event.state.ccnScroll : null;
    go(location.href, { push: false, scroll });
  });

  // Returning from a product page without bfcache: restore the scroll position
  const scrollKey = () => `ccn-scroll:${keyOf(location.href)}`;
  window.addEventListener('pagehide', () => {
    try {
      sessionStorage.setItem(scrollKey(), String(window.scrollY));
    } catch (e) {
      /* ignore */
    }
  });
  try {
    const nav = performance.getEntriesByType('navigation')[0];
    if (nav && nav.type === 'back_forward') {
      const saved = parseFloat(sessionStorage.getItem(scrollKey()) || '');
      if (Number.isFinite(saved) && saved > 0) {
        const restore = () => {
          if (Math.abs(window.scrollY - saved) > 40) window.scrollTo({ top: saved, behavior: 'instant' });
        };
        if (document.readyState === 'complete') restore();
        else window.addEventListener('load', restore, { once: true });
      }
    }
  } catch (e) {
    /* ignore */
  }

  if (!customElements.get('collection-nav')) {
    customElements.define('collection-nav', class extends HTMLElement {});
  }
})();
